import React, { useCallback, useContext, useMemo } from 'react'
import { TranslationContext } from 'contexts'
import { useAppointmentsQuery } from 'client/__generated__/graphql'
import { RegionContext } from 'contexts/RegionsProvider'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import { ScaleLoader } from 'react-spinners'

type Props = {
  navigateCb: (url: string) => void
  headline?: string
}

export default function DatesOverviewComponent(props: Props) {
  // Contexts.
  const { __, langCode } = useContext(TranslationContext)
  const { regions } = useContext(RegionContext) as RegionContext

  const { loading, data } = useAppointmentsQuery({
    variables: {
      status: ['published']
    }
  })

  const locales = {
    de: 'de-DE',
    en: 'en-EN',
    fr: 'fr-FR'
  }

  const userSelectDate = useCallback(
    ({ appointmentId, regionId, subregionId, locationId }) => {
      const mapping = {
        // ! Without trailing slash.
        de: '/de/jetzt-bewerben',
        en: '/en/apply-now',
        fr: '/fr/postulez-maintenant'
      }

      const url = `${mapping[langCode]}/${regionId}/${subregionId}/${locationId}/${appointmentId}/`
      props.navigateCb(url)
    },
    [langCode, props]
  )

  // Memos.
  const regionImages = useMemo(
    () =>
      regions.map((region) => {
        if (!region.icon?.publicURL?.childImageSharp?.gatsbyImageData) return
        const image = getImage(
          region.icon.publicURL.childImageSharp.gatsbyImageData
        )
        if (!image) return
        return {
          id: region.id,
          image: (
            <GatsbyImage
              image={image}
              alt={region.title}
              className="w-8 h-8 mr-3 bg-gray-200 bg-cover bg-center rounded-md"
            />
          )
        }
      }),
    [regions]
  )

  const { allDatesGroupedByCountry, sortedCountryKeys } = useMemo(() => {
    if (!data?.appointments)
      return { allDatesGroupedByCountry: [], sortedCountryKeys: [] }

    const today = new Date(Date.now())

    const allDates = data?.appointments
      .filter(
        (appointment) =>
          new Date(appointment.date) > today &&
          appointment.location_id?.subregion?.region_ref?.title
      )
      .map((appointment) => {
        return {
          id: appointment.id,
          date: new Date(appointment.date),
          title: appointment.title,
          location: appointment.location_id?.title,
          locationId: appointment.location_id?.id,
          countryId: appointment.location_id?.subregion?.region_ref?.id,
          countrySort: appointment.location_id?.subregion?.region_ref?.sort,
          country: appointment.location_id?.subregion?.region_ref?.title,
          region: appointment.location_id?.subregion?.region_ref,
          regionId: appointment.location_id?.subregion?.region_ref?.id,
          subregion: appointment.location_id?.subregion,
          subregionId: appointment.location_id?.subregion?.id
        }
      })
      .sort((a, b) => a.date.getTime() - b.date.getTime())

    const allDatesGroupedByCountry = allDates.reduce((rv, x) => {
      const countryId = x.countryId
      if (!countryId) return rv
      ;(rv[countryId] = rv[countryId] || []).push(x)
      return rv
    }, [])

    return {
      allDatesGroupedByCountry,
      sortedCountryKeys: Object.keys(allDatesGroupedByCountry).sort((a, b) => {
        const aSort = allDatesGroupedByCountry[a][0]?.countrySort
        const bSort = allDatesGroupedByCountry[b][0]?.countrySort
        return aSort - bSort
      })
    }
  }, [data?.appointments])

  if (loading)
    return (
      <div className="flex justify-center">
        <ScaleLoader />
      </div>
    )

  return (
    <div>
      {props.headline && (
        <div className="pb-5 border-b border-gray-200 mt-32">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            {props.headline}
          </h3>
        </div>
      )}
      {sortedCountryKeys.map((countryId, index) => (
        <div key={index}>
          <>
            <h4
              className={[
                'text - lg flex items-center mb-8',
                index === 0 ? 'mt-8' : 'mt-16'
              ].join(' ')}
            >
              {
                regionImages.find((region) => {
                  return region?.id === countryId
                })?.image
              }
              <span>{allDatesGroupedByCountry[countryId][0].country}</span>
            </h4>
          </>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 select-none">
            {allDatesGroupedByCountry[countryId].map((date) => (
              <div
                key={date.id}
                className="group relative flex justify-between items-center gap-5 rounded-md hover:bg-gray-200 p-0 cursor-pointer focus:outline-none bg-gray-100"
                onClick={() =>
                  userSelectDate({
                    appointmentId: date.id,
                    regionId: date.regionId,
                    subregionId: date.subregionId,
                    locationId: date.locationId
                  })
                }
              >
                <div className="min-w-[4rem] h-full flex flex-col items-center justify-center text-center text-sm group-hover:bg-secondary text-black rounded-tl-md rounded-bl-md p-1.5 bg-gray-200">
                  <div className="font-light text-xs opacity-70">
                    {date.date.toLocaleDateString('de-DE', { year: 'numeric' })}
                  </div>
                  <div className="text-3xl -mb-1">
                    {date.date.toLocaleDateString(
                      locales[langCode] || locales['en'],
                      { day: 'numeric' }
                    )}
                  </div>
                  <div className="font-light text-base opacity-70">
                    {date.date
                      .toLocaleDateString(locales[langCode] || locales['en'], {
                        month: 'short'
                      })
                      .toUpperCase()}
                  </div>
                </div>
                <div className="grow py-4 pr-4">
                  <div className="text-gray-900 font-bold">{date.title}</div>
                  <div className="text-gray-500 text-sm">{date.location}</div>
                  <div
                    className="
                      absolute
                      hidden
                      group-hover:block
                      text-primary
                      underline-offset-2
                      underline
                      right-2.5
                      bottom-1.5
                      text-sm"
                  >
                    {__('__TERMIN_AUSWAEHLEN__')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
